export const APPLICATION_QUESTION_TYPES = [
  { value: "short_text", label: "Short answer" },
  { value: "long_text", label: "Long answer" },
  { value: "single_choice", label: "Single choice" },
  { value: "multi_choice", label: "Multiple choice" },
  { value: "yes_no", label: "Yes / No" }
];

export const FIREARMS_SCREENING_TEMPLATE = {
  enabled: false,
  status: "draft",
  version: 1,
  title: "Membership Application",
  description: "Firearms background, legal and safety screening, interests, and competitive event preferences.",
  sections: [
    {
      sectionId: "firearms_background",
      title: "Firearms Background",
      description: "",
      questions: [
        {
          questionId: "firearms_experience",
          label: "Years of Experience with Firearms",
          type: "single_choice",
          required: true,
          helpText: "",
          options: [
            { optionId: "none", label: "None" },
            { optionId: "less_than_1_year", label: "Less than 1 year" },
            { optionId: "1_3_years", label: "1-3 years" },
            { optionId: "4_10_years", label: "4-10 years" },
            { optionId: "10_plus_years", label: "10+ years" }
          ],
          maxSelections: 0,
          allowOther: false
        },
        {
          questionId: "formal_training",
          label: "List any formal firearms training classes, certifications, or qualifications",
          type: "long_text",
          required: false,
          helpText: "Examples: NRA, USCCA, law enforcement/military, concealed carry, etc.",
          options: [],
          maxSelections: 0,
          allowOther: false
        }
      ]
    },
    {
      sectionId: "legal_safety_screening",
      title: "Legal & Safety Screening",
      description: "",
      questions: [
        {
          questionId: "felony_or_violent_misdemeanor",
          label: "Have you ever been convicted of a felony or violent misdemeanor?",
          type: "yes_no",
          required: true,
          helpText: "",
          options: [],
          maxSelections: 0,
          allowOther: false,
          conditionalDetails: {
            enabled: true,
            whenValue: "yes",
            label: "If yes, please provide details",
            required: true
          }
        }
      ]
    },
    {
      sectionId: "areas_of_interest",
      title: "Areas of Interest",
      description: "Select your top three activities that interest you most.",
      questions: [
        {
          questionId: "top_activities",
          label: "Select your top three activities",
          type: "multi_choice",
          required: true,
          helpText: "",
          maxSelections: 3,
          allowOther: false,
          options: [
            "Long Range Precision",
            "Defensive Pistol",
            "Tactical Pistol",
            "Cowboy Action Shooting",
            "Tactical Rifle / Carbine",
            "Hunting",
            "Clay Shooting (Trap, Skeet, Sporting Clays)",
            "Rimfire / Plinking",
            "Black Powder / Muzzleloader",
            "Archery",
            "3-Gun / Multigun",
            "IDPA",
            "USPSA Practical Shooting",
            "High Power Rifle",
            "Silhouette Shooting"
          ].map((label) => ({ optionId: slugifyFormKey(label), label }))
        }
      ]
    },
    {
      sectionId: "competitive_events",
      title: "Competitive Events",
      description: "Select all that apply.",
      questions: [
        {
          questionId: "competitive_events",
          label: "Competitive events",
          type: "multi_choice",
          required: false,
          helpText: "",
          maxSelections: 0,
          allowOther: true,
          options: [
            "NRL (National Rifle League)",
            "NRL22",
            "Steel Challenge",
            "IPSC",
            "PRS (Precision Rifle Series)",
            "F-Class",
            "Benchrest",
            "Cowboy Action / Quick Draw",
            "USPSA",
            "IDPA",
            "3-Gun / Multigun",
            "NRA High Power",
            "Rimfire Challenge",
            "Action Pistol"
          ].map((label) => ({ optionId: slugifyFormKey(label), label }))
        }
      ]
    }
  ]
};

export function slugifyFormKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

export function createBlankApplicationForm() {
  return {
    enabled: false,
    status: "draft",
    version: 1,
    title: "Membership Application",
    description: "",
    sections: []
  };
}

export function normalizeApplicationFormForClient(form = {}) {
  const source = form && typeof form === "object" && !Array.isArray(form)
    ? form
    : {};
  return {
    ...createBlankApplicationForm(),
    ...source,
    enabled: Boolean(source.enabled),
    status: source.status || (source.sections?.length ? "draft" : "not_configured"),
    version: Number.parseInt(source.version || 1, 10) || 1,
    sections: Array.isArray(source.sections)
      ? source.sections.map((section, sectionIndex) => ({
          sectionId: section.sectionId || `section_${sectionIndex + 1}`,
          title: section.title || `Section ${sectionIndex + 1}`,
          description: section.description || "",
          questions: Array.isArray(section.questions)
            ? section.questions.map((question, questionIndex) => ({
                questionId: question.questionId || `question_${questionIndex + 1}`,
                label: question.label || `Question ${questionIndex + 1}`,
                type: question.type || "short_text",
                required: question.required !== false,
                helpText: question.helpText || "",
                options: Array.isArray(question.options) ? question.options : [],
                maxSelections: Number.parseInt(question.maxSelections || 0, 10) || 0,
                allowOther: Boolean(question.allowOther),
                conditionalDetails: question.conditionalDetails?.enabled
                  ? {
                      enabled: true,
                      whenValue: question.conditionalDetails.whenValue || "yes",
                      label: question.conditionalDetails.label || "Please provide details",
                      required: Boolean(question.conditionalDetails.required)
                    }
                  : undefined
              }))
            : []
        }))
      : []
  };
}

export function applicationFormQuestionCount(form = {}) {
  return (form.sections || []).reduce((count, section) => count + (section.questions || []).length, 0);
}

export function applicationFormIsPublished(form = {}) {
  return Boolean(form.enabled && form.status === "published" && applicationFormQuestionCount(form) > 0);
}

export function applicationFormSectionsWithResponses(form = {}, responses = []) {
  const responseByQuestion = new Map((responses || []).map((response) => [response.questionId, response]));
  return (form.sections || []).map((section) => ({
    ...section,
    questions: (section.questions || []).map((question) => ({
      ...question,
      response: responseByQuestion.get(question.questionId)
    }))
  }));
}

export function applicationAnswerDisplay(response = {}) {
  if (!response) {
    return "";
  }
  if (response.displayValue) {
    return response.details ? `${response.displayValue} - ${response.details}` : response.displayValue;
  }
  if (Array.isArray(response.value)) {
    return response.value.join(", ");
  }
  return String(response.value || "");
}
